import React, { useState, useCallback } from 'react';

type CloneRepoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImport: (files: Record<string, string>) => void;
};

type RepoInfo = {
  owner: string;
  repo: string;
  branch: string;
  path: string;
  provider: 'github' | 'gitlab' | 'bitbucket';
};

export const CloneRepoModal = React.memo(function CloneRepoModal({
  isOpen,
  onClose,
  onImport,
}: CloneRepoModalProps) {
  const [repoUrl, setRepoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string>('');

  // Parse repository URL
  const parseRepoUrl = useCallback((url: string): RepoInfo | null => {
    // GitHub patterns
    // https://github.com/owner/repo
    // https://github.com/owner/repo/tree/branch
    // https://github.com/owner/repo/tree/branch/path/to/dir
    // git@github.com:owner/repo.git
    const githubHttps = url.match(/github\.com[\/:]([^\/]+)\/([^\/\s]+?)(?:\.git)?(?:\/tree\/([^\/]+)(?:\/(.+))?)?$/);
    if (githubHttps) {
      return {
        owner: githubHttps[1],
        repo: githubHttps[2].replace(/\.git$/, ''),
        branch: githubHttps[3] || 'main',
        path: githubHttps[4] || '',
        provider: 'github',
      };
    }

    // GitLab patterns
    // https://gitlab.com/owner/repo
    // https://gitlab.com/owner/repo/-/tree/branch
    const gitlabHttps = url.match(/gitlab\.com\/([^\/]+)\/([^\/\s]+?)(?:\.git)?(?:\/-\/tree\/([^\/]+)(?:\/(.+))?)?$/);
    if (gitlabHttps) {
      return {
        owner: gitlabHttps[1],
        repo: gitlabHttps[2].replace(/\.git$/, ''),
        branch: gitlabHttps[3] || 'main',
        path: gitlabHttps[4] || '',
        provider: 'gitlab',
      };
    }

    // Bitbucket patterns
    // https://bitbucket.org/owner/repo
    // https://bitbucket.org/owner/repo/src/branch
    const bitbucketHttps = url.match(/bitbucket\.org\/([^\/]+)\/([^\/\s]+?)(?:\.git)?(?:\/src\/([^\/]+)(?:\/(.+))?)?$/);
    if (bitbucketHttps) {
      return {
        owner: bitbucketHttps[1],
        repo: bitbucketHttps[2].replace(/\.git$/, ''),
        branch: bitbucketHttps[3] || 'main',
        path: bitbucketHttps[4] || '',
        provider: 'bitbucket',
      };
    }

    return null;
  }, []);

  // Fetch files from GitHub
  const fetchGitHubFiles = useCallback(async (
    owner: string,
    repo: string,
    branch: string,
    path: string
  ): Promise<Record<string, string>> => {
    const files: Record<string, string> = {};

    const fetchDir = async (dirPath: string) => {
      const url = `https://api.github.com/repos/${owner}/${repo}/contents/${dirPath}?ref=${branch}`;
      setProgress(`Fetching: ${dirPath || '/'}`);

      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Repository or path not found. Check if the URL is correct and the repository is public.`);
        }
        throw new Error(`GitHub API error: ${response.status}`);
      }

      const items = await response.json();

      for (const item of items) {
        if (item.type === 'file') {
          // Skip large files and binary files
          if (item.size > 500000) continue;
          const ext = item.name.split('.').pop()?.toLowerCase() || '';
          const binaryExts = ['png', 'jpg', 'jpeg', 'gif', 'ico', 'svg', 'woff', 'woff2', 'ttf', 'eot', 'mp3', 'mp4', 'webm', 'pdf', 'zip', 'tar', 'gz'];
          if (binaryExts.includes(ext)) continue;

          setProgress(`Downloading: ${item.path}`);
          const fileResponse = await fetch(item.download_url);
          if (fileResponse.ok) {
            const content = await fileResponse.text();
            const relativePath = path ? item.path.replace(path + '/', '') : item.path;
            files[relativePath] = content;
          }
        } else if (item.type === 'dir') {
          // Skip node_modules, .git, and other large directories
          const skipDirs = ['node_modules', '.git', 'dist', 'build', '.next', 'coverage', '.cache'];
          if (!skipDirs.includes(item.name)) {
            await fetchDir(item.path);
          }
        }
      }
    };

    await fetchDir(path);
    return files;
  }, []);

  // Fetch files from GitLab
  const fetchGitLabFiles = useCallback(async (
    owner: string,
    repo: string,
    branch: string,
    path: string
  ): Promise<Record<string, string>> => {
    const files: Record<string, string> = {};
    const projectId = encodeURIComponent(`${owner}/${repo}`);

    const fetchDir = async (dirPath: string) => {
      const encodedPath = dirPath ? encodeURIComponent(dirPath) : '';
      const url = `https://gitlab.com/api/v4/projects/${projectId}/repository/tree?ref=${branch}&path=${encodedPath}&per_page=100`;
      setProgress(`Fetching: ${dirPath || '/'}`);

      const response = await fetch(url);
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`Repository or path not found. Check if the URL is correct and the repository is public.`);
        }
        throw new Error(`GitLab API error: ${response.status}`);
      }

      const items = await response.json();

      for (const item of items) {
        if (item.type === 'blob') {
          const ext = item.name.split('.').pop()?.toLowerCase() || '';
          const binaryExts = ['png', 'jpg', 'jpeg', 'gif', 'ico', 'svg', 'woff', 'woff2', 'ttf', 'eot', 'mp3', 'mp4', 'webm', 'pdf', 'zip', 'tar', 'gz'];
          if (binaryExts.includes(ext)) continue;

          setProgress(`Downloading: ${item.path}`);
          const fileUrl = `https://gitlab.com/api/v4/projects/${projectId}/repository/files/${encodeURIComponent(item.path)}/raw?ref=${branch}`;
          const fileResponse = await fetch(fileUrl);
          if (fileResponse.ok) {
            const content = await fileResponse.text();
            const relativePath = path ? item.path.replace(path + '/', '') : item.path;
            files[relativePath] = content;
          }
        } else if (item.type === 'tree') {
          const skipDirs = ['node_modules', '.git', 'dist', 'build', '.next', 'coverage', '.cache'];
          if (!skipDirs.includes(item.name)) {
            await fetchDir(item.path);
          }
        }
      }
    };

    await fetchDir(path);
    return files;
  }, []);

  // Clone repository
  const handleClone = useCallback(async () => {
    if (!repoUrl.trim()) return;

    const repoInfo = parseRepoUrl(repoUrl.trim());
    if (!repoInfo) {
      setError('Invalid repository URL. Supported: GitHub, GitLab, Bitbucket');
      return;
    }

    setLoading(true);
    setError(null);
    setProgress('Initializing...');

    try {
      let files: Record<string, string> = {};

      if (repoInfo.provider === 'github') {
        files = await fetchGitHubFiles(repoInfo.owner, repoInfo.repo, repoInfo.branch, repoInfo.path);
      } else if (repoInfo.provider === 'gitlab') {
        files = await fetchGitLabFiles(repoInfo.owner, repoInfo.repo, repoInfo.branch, repoInfo.path);
      } else if (repoInfo.provider === 'bitbucket') {
        // Bitbucket API is more complex, redirect to GitHub Import for now
        setError('Bitbucket support coming soon. Please use GitHub or GitLab.');
        setLoading(false);
        return;
      }

      const fileCount = Object.keys(files).length;
      if (fileCount === 0) {
        throw new Error('No files found in the repository. Check if the path is correct.');
      }

      setProgress(`Successfully cloned ${fileCount} files!`);
      onImport(files);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clone repository');
    } finally {
      setLoading(false);
    }
  }, [repoUrl, parseRepoUrl, fetchGitHubFiles, fetchGitLabFiles, onImport]);

  const handleClose = useCallback(() => {
    setRepoUrl('');
    setError(null);
    setProgress('');
    onClose();
  }, [onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      handleClone();
    }
  }, [handleClone, loading]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <div className="modal-header">
          <div className="modal-title">Clone Repository</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
              Repository URL:
            </label>
            <input
              type="text"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="https://github.com/owner/repo"
              disabled={loading}
              style={{
                width: '100%',
                padding: '10px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                borderRadius: '4px',
                color: 'var(--text)',
                fontSize: '13px',
              }}
            />
          </div>

          <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
            <div style={{ fontSize: '11px', color: 'var(--muted)', marginBottom: '8px' }}>
              <strong>Supported URL formats:</strong>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)', lineHeight: '1.6' }}>
              <div><code style={{ background: 'var(--bg)', padding: '1px 4px', borderRadius: '2px' }}>https://github.com/owner/repo</code></div>
              <div><code style={{ background: 'var(--bg)', padding: '1px 4px', borderRadius: '2px' }}>https://github.com/owner/repo/tree/branch</code></div>
              <div><code style={{ background: 'var(--bg)', padding: '1px 4px', borderRadius: '2px' }}>https://gitlab.com/owner/repo</code></div>
              <div><code style={{ background: 'var(--bg)', padding: '1px 4px', borderRadius: '2px' }}>git@github.com:owner/repo.git</code></div>
            </div>
          </div>

          {error && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(255, 100, 100, 0.1)',
              border: '1px solid var(--error)',
              borderRadius: '4px',
              marginBottom: '16px',
              color: 'var(--error)',
              fontSize: '12px',
            }}>
              {error}
            </div>
          )}

          {loading && progress && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid var(--accent)',
              borderRadius: '4px',
              marginBottom: '16px',
              color: 'var(--accent)',
              fontSize: '12px',
            }}>
              {progress}
            </div>
          )}

          <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '8px' }}>
            <strong>Note:</strong> Only public repositories are supported. Binary files and node_modules are skipped.
          </div>
        </div>

        <div className="modal-footer">
          <button className="pill-btn" onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button
            className="pill-btn"
            onClick={handleClone}
            disabled={loading || !repoUrl.trim()}
            style={{
              background: 'var(--accent)',
              color: 'white',
              opacity: loading || !repoUrl.trim() ? 0.5 : 1,
            }}
          >
            {loading ? 'Cloning...' : 'Clone'}
          </button>
        </div>
      </div>
    </div>
  );
});

export default CloneRepoModal;
